import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SectionHeading, track } from "./_shared";

const PRICING = [
  { key: "starter", name: "Starter", price: "$180", unit: "/mo flat",
    tag: "Up to 10 active clients",
    desc: "Moving off paper or Excel.",
    features: [
      "AI clinical notes & voice dictation",
      "Geo clock-in (EVV-ready)",
      "MAR & vitals · Family portal",
      "Bulk PDF audit exports",
      "Offline mobile · BAA included",
    ],
    cta: "Start with Starter", to: "/agency/signup?plan=starter" },
  { key: "growth", name: "Growth", price: "$17", unit: "/active client/mo",
    tag: "11–30 active clients",
    desc: "The small-agency sweet spot.",
    featured: true, badge: "Most popular",
    features: [
      "Everything in Starter, plus:",
      "Drag-and-drop scheduling",
      "Open-shift broadcast",
      "Timecards & payroll CSV (Gusto, QBO, ADP)",
      "Stripe invoicing · ATS · white-label portal",
    ],
    cta: "Try Growth free 14 days", to: "/agency/signup?plan=growth" },
  { key: "scale", name: "Scale", price: "$15", unit: "/active client/mo",
    tag: "31–75 active clients",
    desc: "Growing agencies, volume rate.",
    features: [
      "Everything in Growth, plus:",
      "Priority support",
      "Migration assistance",
      "Custom note templates",
      "Dedicated onboarding",
    ],
    cta: "Talk to us", to: "mailto:hello@afyanex.com" },
];

export default function LandingPricing() {
  return (
    <section id="pricing" className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <SectionHeading
        eyebrow="Transparent pricing"
        sub="Caregivers fluctuate. Your client roster doesn't. Only active clients (one+ visit that month) count."
      >
        Priced per <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">active client</span> — not per caregiver.
      </SectionHeading>
      <div className="grid lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {PRICING.map((p) => (
          <div key={p.key}
               data-testid={`landing-pricing-${p.key}`}
               className={`relative rounded-2xl p-7 flex flex-col ${
                 p.featured
                   ? "bg-white border-2 border-afyanex-pink shadow-[0_30px_80px_-30px_rgba(255,70,163,0.4)]"
                   : "bg-white border border-clinical-border/70"
               }`}>
            {p.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-afyanex-pink text-white text-[10px] uppercase tracking-[0.18em] font-semibold">
                {p.badge}
              </div>
            )}
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-clinical-subtext">{p.tag}</div>
            <h3 className="mt-2 font-heading text-2xl font-semibold text-clinical-text">{p.name}</h3>
            <p className="mt-1 text-[14px] text-clinical-subtext">{p.desc}</p>
            <div className="mt-5 flex items-baseline gap-1">
              <span className="font-heading text-4xl font-semibold text-clinical-text">{p.price}</span>
              <span className="text-sm text-clinical-subtext">{p.unit}</span>
            </div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[14px]">
                  <Check className="h-4 w-4 text-afyanex-blue mt-0.5 shrink-0" />
                  <span className={f.startsWith("Everything") ? "font-medium text-clinical-text" : "text-clinical-text"}>{f}</span>
                </li>
              ))}
            </ul>
            <Link to={p.to}
                  onClick={() => track("pricing_tier_clicked", { source: "landing", tier: p.key, destination: p.to })}
                  className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-all ${
                    p.featured
                      ? "bg-afyanex-pink text-white shadow-[0_10px_28px_-12px_rgba(255,70,163,0.55)] hover:-translate-y-px"
                      : "border border-afyanex-blue/30 text-afyanex-blue hover:bg-afyanex-blue hover:text-white"
                  }`}
                  data-testid={`landing-pricing-cta-${p.key}`}>
              {p.cta} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        ))}
      </div>
      <p className="mt-8 text-center text-xs text-clinical-subtext max-w-2xl mx-auto">
        All plans include unlimited admins · email & in-app support · no setup
        fees · month-to-month or annual (saves 2 months).
      </p>
    </section>
  );
}
